import './AurigaContext.scss';

import Notification, { NotificationProps } from '@components/Common/Notification';
import { Alert } from '@mui/material';
import Box from '@mui/material/Box';
import { createId } from '@paralleldrive/cuid2';
import { setGlobalErrorHandler } from '@utils/errorUtils';
import { setQueryLatencyLogger } from '@utils/rum/rumQueryLatency';
import { cloneDeep, find, isEmpty, isEqual, isNil } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AurigaContextProviderProps,
  AurigaContextType,
  AurigaFeatureFlags,
  SystemAlert,
  ToastSettings,
} from 'types/AurigaContext.types';

/**
 * Auriga Application General Context
 *
 * React Context for Application wide utility exposure and general state management
 */
export const AurigaContext = createContext<AurigaContextType>({
  toastNotification: () => {},
  closeToastNotification: () => {},
  showAlert: () => '',
  dismissAlert: () => {},
  clearAllAlerts: () => {},
  checkDuplicateAlert: () => undefined,
  renderAlerts: () => null,
  featureFlags: {
    enableFootnotes: false, // Flags set to false by default
    enableAuditHistory: false,
    enableComments: false,
    enableDownloadPage: false,
    enableKPILibraryCreate: false,
    enableCalcMembersSave: false,
    enableSharedExcelExport: false,
  },
  rumLogger: null,
});

/**
 * Auriga Provider Component
 *
 * This component provides Auriga context additions to the application.
 * It manages the state and handling application-wide toasts, and is general
 * enough to accommodate future additions that cut across views in the application.
 *
 * @param {AurigaContextProviderProps} props - The provider component props
 * @returns {JSX.Element} The provider component
 */
export const AurigaContextProvider = ({ children, rumLogger }: AurigaContextProviderProps): ReactNode => {
  /**
   * Constants to control how Toasts look and are positioned
   */
  const TOAST_SETTINGS: ToastSettings = useMemo(
    () => ({
      // Vertical spacing between elements in px
      SPACING: 80,
      // Used for dynamic position offset in viewport
      POSITION_OFFSET: 24,
      // To smooth removing from view. Configurable as a fraction of a seconds (s)
      TRANSITION_DURATION: 0.3,
      // To restrict the number of messages that can exist for pending toasts at any time
      // Exceeding this will cause removal of old notifications
      MAX_TOASTS: 5,
    }),
    [],
  );

  // For simple notification tracking at the context level for Auriga
  const [notifications, setNotifications] = useState<NotificationProps[]>([]);
  const notificationsRef = useRef<NotificationProps[]>([]);
  // For system-wide alerts
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const alertsRef = useRef<SystemAlert[]>([]);

  /**
   * Feature flags configuration
   * TODO: Enable or disable any new feature flags as needed
   */
  const featureFlags: AurigaFeatureFlags = useMemo(
    () => ({
      enableFootnotes: true,
      enableAuditHistory: true,
      enableComments: true,
      enableDownloadPage: true,
      // Permits creating Root/Variant KPIs:
      enableKPILibraryCreate: true,
      // OFF until the backend accepts calc_members on SaveDeck's DecklineItemsInput (handoff §8 step 3).
      // While OFF the FE saves the baked kpi_id/kpi_formula and omits calc_members from the save.
      enableCalcMembersSave: false,
      // ON by default (2026-10-27), so the team tests the shared Excel export without turning on the per-browser
      // toggle -- manual testing turned up no issues. A dev-team admin can still switch their own browser back
      // to the legacy export from the Admin panel, to compare the two.
      enableSharedExcelExport: true,
    }),
    [],
  );

  /**
   * Updates notificationsRef when notifications state changes.
   */
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  /**
   * Updates alertsRef and alerts state.
   *
   * This was implemented to better support React.StrictMode double-rendering.
   * @param newList - new list of Alerts.
   */
  const updateAlerts = useCallback((newList: SystemAlert[]) => {
    alertsRef.current = newList;
    setAlerts(newList);
  }, []);

  /**
   * Handles clearing out the current notification object when it is closed
   * @param notificationId - ID of the notification to close (optional)
   */
  const handleCloseNotification = useCallback((notificationId?: string) => {
    if (notificationId) {
      setNotifications((prevNotifications) => {
        return [...prevNotifications.filter((entry) => !isEqual(entry?.notificationId, notificationId))];
      });
    } else {
      // Assume intent to clear all notifications
      setNotifications([]);
    }
  }, []);

  /**
   * Simple Notification invoker to simplify toasting a notification
   * @param {NotificationProps} message - Notification message to toast
   */
  const toastNotification = useCallback(
    (message: NotificationProps) => {
      const { MAX_TOASTS } = TOAST_SETTINGS;
      const notification = cloneDeep(message);
      const { onClose, notificationId: propsNotificationId } = notification;
      // Assign a unique notificationId if one was not received
      const notificationId = propsNotificationId || createId();
      notification.notificationId = notificationId;

      // Add a handler to remove the notification from the tracking array, and
      // also to invoke any received 'onClose' property
      notification.onClose = () => {
        handleCloseNotification(notificationId);
        if (onClose) {
          onClose();
        }
      };

      // Add the new message to the notifications array to render in the DOM
      setNotifications((prevNotifications) => {
        const newNotifications = [
          ...prevNotifications.filter((entry) => !isEqual(entry?.notificationId, notificationId)),
        ];
        if (newNotifications.length >= MAX_TOASTS) {
          // Remove the oldest notifications
          newNotifications.shift();
        }
        newNotifications.push(notification);
        return newNotifications;
      });
    },
    [TOAST_SETTINGS, handleCloseNotification],
  );

  /**
   * Renders the toasts for notifications in the DOM with stacked positioning overrides
   */
  const renderToasts = useCallback(() => {
    if (!isNil(notifications) && !isEmpty(notifications)) {
      return notifications.map((notification, idx) => {
        const { notificationId } = notification;
        const { POSITION_OFFSET, SPACING, TRANSITION_DURATION } = TOAST_SETTINGS;

        /** NOTE: Due to the dynamic need of stacking, this Box wrapper provides for an exception use-case
         to allow dynamic stacking of each toast on top of one another through the styles property. */
        return (
          <Box
            key={`toast-stack-wrapper-${notificationId || idx}`}
            style={{
              position: 'fixed',
              bottom: `${idx === 0 ? POSITION_OFFSET : POSITION_OFFSET + idx * SPACING}px`,
              right: `${POSITION_OFFSET}px`,
              zIndex: 9999 + idx,
              transition: `all ${TRANSITION_DURATION}s ease-out`, // Smooth transition some
            }}
          >
            <Notification
              key={`toast-${notificationId || idx}`}
              {...notification}
              style={{
                position: 'relative',
              }}
            />
          </Box>
        );
      });
    }

    return null;
  }, [TOAST_SETTINGS, notifications]);

  /**
   * Check for duplicate notifications
   * @param {string} message - Notification message to check for duplicates
   * @returns {string | undefined} Duplicate notification ID if found, undefined otherwise
   */
  const checkDuplicateNotification = useCallback((message: string) => {
    const duplicateNotification = find(notificationsRef.current, (existingNotification) =>
      isEqual(existingNotification?.message, message),
    );

    return duplicateNotification?.notificationId;
  }, []); // No notifications dependency so that function is stable

  /**
   * Register global error handler for centralized error handling
   */
  useEffect(() => {
    setGlobalErrorHandler((message: string) => {
      // Check if a notification with the same message already exists.
      // This will toast only a single Error message of the same message text once to avoid repeated
      // Error messages in multiple api calls (such as when populating numerous GetNodeDetails with DimSum failures)
      const existingNotificationId = checkDuplicateNotification(message);
      if (!existingNotificationId) {
        toastNotification({
          message,
          visible: true,
          type: 'error',
          duration: 10000, // Keeps global errors open for 10 seconds for readability
        });
      }
    });
  }, [checkDuplicateNotification, toastNotification]);

  /**
   * Register the RUM logger used by the global QueryCache/MutationCache latency handlers
   * (mirrors setGlobalErrorHandler above). Keeps the QueryClient — created outside React in
   * index.tsx — decoupled from context.
   */
  useEffect(() => {
    setQueryLatencyLogger(rumLogger);
    return () => setQueryLatencyLogger(null);
  }, [rumLogger]);

  /**
   * Check for duplicate alerts
   * @param {Omit<SystemAlert, 'id'>} alert - Alert to check for duplicates
   * @returns {string | undefined} Duplicate alert ID if found, undefined otherwise
   */
  const checkDuplicateAlert = useCallback(
    (alert: Omit<SystemAlert, 'id'>) => {
      // Check if an alert with the same message, type, and alertRoutes already exists
      const duplicateAlert = find(
        alertsRef.current, // Use ref to get current alerts without dependency
        (existingAlert) =>
          existingAlert.message === alert.message &&
          existingAlert.type === alert.type &&
          isEqual(existingAlert.alertRoutes, alert.alertRoutes),
      );

      return duplicateAlert?.id;
    },
    [], // No alerts dependency so that function is stable (avoids endless loop scenarios)
  );

  /**
   * Show system alert
   * @param {Omit<SystemAlert, 'id'>} alert - Alert to show ('id' gets auto-generated)
   * @returns {string} Alert ID for dismissing later
   */
  const showAlert = useCallback(
    (alert: Omit<SystemAlert, 'id'>): string => {
      // Check if an alert with the same message, type, and alertRoutes already exists
      const existingAlertId = checkDuplicateAlert(alert);
      if (existingAlertId) {
        return existingAlertId; // Return existing alert ID
      }

      // Creates the new System Alert, as it does not already exist
      const alertId = createId();

      updateAlerts([
        ...alertsRef.current,
        {
          ...alert,
          id: alertId,
        },
      ]);

      return alertId;
    },
    [checkDuplicateAlert, updateAlerts],
  );

  /**
   * Clear all system alerts
   */
  const clearAllAlerts = useCallback(() => {
    updateAlerts([]);
  }, [updateAlerts]);

  /**
   * Dismiss system alert
   * @param {string} alertId - ID of alert to dismiss
   */
  const dismissAlert = useCallback(
    (alertId: string) => {
      updateAlerts(alertsRef.current.filter((alert) => alert.id !== alertId));
    },
    [updateAlerts],
  );

  /**
   * Renders system alerts
   */
  const renderAlerts = useCallback(
    (currentRoute?: string) => {
      const visibleAlerts = alerts.filter((alert) => {
        if (!alert.visible) return false;

        // If no alertRoutes specified, show on all routes (backward compatibility)
        if (!alert.alertRoutes) return true;

        // If alertRoutes includes 'all', show globally
        if (alert.alertRoutes.includes('all')) return true;

        // If currentRoute specified, check if it matches any alertRoutes
        if (currentRoute) {
          return alert.alertRoutes.some((route) => currentRoute.includes(route));
        }

        return true;
      });

      if (!isEmpty(visibleAlerts)) {
        return (
          <Box className='system-alerts'>
            {visibleAlerts.map((alert) => (
              <Alert
                key={alert.id}
                severity={alert.type}
                variant={alert.variant}
                onClose={
                  alert.dismissible !== false
                    ? () => {
                        if (alert.onClose) alert.onClose();
                        dismissAlert(alert.id);
                      }
                    : undefined
                }
                icon={alert.icon}
                className='system-alert'
              >
                {alert.message}
              </Alert>
            ))}
          </Box>
        );
      }

      return null;
    },
    [alerts, dismissAlert],
  );

  /**
   * Create the context value object
   *
   * Combines all state variables and methods into a single object that will be
   * provided to consumers of the context. This ensures that the context value
   * only changes when one of its dependencies changes, preventing unnecessary
   * re-renders of components that consume the context.
   */
  const contextValue = useMemo<AurigaContextType>(
    () => ({
      toastNotification,
      closeToastNotification: handleCloseNotification,
      showAlert,
      dismissAlert,
      clearAllAlerts,
      checkDuplicateAlert,
      renderAlerts,
      featureFlags,
      rumLogger,
    }),
    [
      toastNotification,
      handleCloseNotification,
      showAlert,
      dismissAlert,
      clearAllAlerts,
      checkDuplicateAlert,
      renderAlerts,
      featureFlags,
      rumLogger,
    ],
  );

  return (
    <AurigaContext value={contextValue}>
      {children}
      {renderToasts()}
    </AurigaContext>
  );
};

/**
 * Custom hook to access the Auriga context
 *
 * @throws {Error} If used outside AurigaContextProvider
 * @returns {AurigaContextType} The Auriga context value
 */
export const useAurigaContext = (): AurigaContextType => use(AurigaContext);
